import { useState } from "react";
import { createCheckoutSession } from "../../services/UserService";
import { Button } from "../Button";
import BaseModal from "./BaseModal";

interface PremiumUpsellModalProps {
  open: boolean;
  onClose: () => void;
}

export default function PremiumUpsellModal({ open, onClose }: PremiumUpsellModalProps) {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');

  const handleSubscribe = async () => {
    setLoading(true);
    setCouponError(''); // Clear any previous errors

    // Track conversion with Facebook Pixel
    try {
      if (typeof window.fbq !== 'undefined') {
        window.fbq('track', 'InitiateCheckout', { value: selectedPlan });
      }
    } catch (e) {
      // Ignore errors with fbq
      console.log("Error tracking with Facebook Pixel:", e);
    }

    try {
      const checkoutSession = await createCheckoutSession(selectedPlan, couponCode);
      window.location.href = checkoutSession.data.checkoutUrl;
    } catch (error: any) {
      setLoading(false);
      
      // Handle coupon-specific errors
      if (error.response?.status === 400 && error.response?.data?.error === "Invalid coupon code") {
        setCouponError('Cupom inválido');
      } else {
        // Handle other errors (you might want to show a general error message)
        console.error('Checkout error:', error);
      }
    }
  };

  const premiumFeatures = [
    "Acesso a todos os cursos e conteúdos",
    "Importação de transações bancárias via OFX",
    "Comunidade exclusiva de suporte",
    "Acompanhamento de metas",
    "Dashboards",
    "Simuladores financeiros",
    "Construção de orçamento e fluxo de caixa",
  ];

  return (
    <BaseModal className={"p-0 pb-4 sm:p-0 sm:pb-4"} open={open} onClose={() => { }}>
      <div className="flex flex-col items-center text-center">
        {/* Header with gradient background - Fixed to take full width */}
        <div className="w-full px-8 py-5 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-t-xl">
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            Eleve suas finanças ao próximo nível
          </h2>
        </div>

        <div className="px-4 sm:px-6 w-full">

          {/* Features list */}
          <div className="mt-4 w-full">
            <ul className="text-left space-y-2">
              {premiumFeatures.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <svg
                    className="h-5 w-5 text-green-500 mr-2 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Plan selection */}
          <div className="mt-6 w-full">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Escolha seu plano:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Monthly Plan */}
              <div
                className={`border rounded-lg p-4 cursor-pointer transition-all ${selectedPlan === 'monthly'
                  ? 'border-blue-600 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-blue-400'
                  }`}
                onClick={() => setSelectedPlan('monthly')}
              >
                <div className="flex items-start mb-2">
                  <div
                    className={`w-5 h-5 rounded-full border flex-shrink-0 mr-2 mt-1 flex items-center justify-center ${selectedPlan === 'monthly' ? 'border-blue-600' : 'border-gray-400'
                      }`}
                  >
                    {selectedPlan === 'monthly' && (
                      <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Plano Mensal</h4>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      R$ 20<span className="text-sm font-normal text-gray-500">/mês</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Acesso a todos os recursos premium
                    </p>
                  </div>
                </div>
              </div>

              {/* Annual Plan with discount */}
              <div
                className={`border rounded-lg p-4 cursor-pointer transition-all relative ${selectedPlan === 'annual'
                  ? 'border-blue-600 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-blue-400'
                  }`}
                onClick={() => setSelectedPlan('annual')}
              >
                {/* Badge */}
                <div className="absolute -top-3 -right-3 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  Melhor valor
                </div>

                <div className="flex items-start mb-2">
                  <div
                    className={`w-5 h-5 rounded-full border flex-shrink-0 mr-2 mt-1 flex items-center justify-center ${selectedPlan === 'annual' ? 'border-blue-600' : 'border-gray-400'
                      }`}
                  >
                    {selectedPlan === 'annual' && (
                      <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Plano Anual</h4>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      R$ 12,50<span className="text-sm font-normal text-gray-500">/mês</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs line-through text-gray-400">R$ 240/ano</span>
                      <span className="text-sm font-semibold text-green-600">Economize 37,5%</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Faturado anualmente como R$ 150,00
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Coupon Code Input */}
          <div className="mt-6 w-full">
            <div className="flex flex-col gap-2">
              <label htmlFor="coupon" className="text-sm font-medium text-gray-700 text-left">
                Código de cupom (opcional)
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="coupon"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value.toUpperCase());
                    setCouponError(''); // Clear error when user types
                  }}
                  placeholder="Digite seu código de cupom"
                  className={`w-full h-10 px-3 py-2 border rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                    couponError 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {couponCode && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg
                      className="h-4 w-4 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              {couponError && (
                <p className="text-xs text-red-600 mt-1">
                  {couponError}
                </p>
              )}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="mt-6 space-y-3">
            <Button
              onClick={handleSubscribe}
              title={`Assinar ${selectedPlan === 'annual' ? 'Plano Anual' : 'Plano Mensal'}`}
              loading={loading}
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium text-lg rounded-lg"
            />
            <button
              onClick={onClose}
              className="w-full py-2 text-gray-600 hover:text-gray-800 font-medium text-sm"
            >
              Continuar com plano gratuito
            </button>
          </div>

        </div>
      </div>
    </BaseModal>
  );
}